import dayjs from 'utils/dayjsConfig';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat'];
const FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

/**
 * Returns day of the month with correct suffix (st, nd, rd, th, ex 3rd of the month)
 * @param {num} day - The day of the month you wish the append the suffix to
 * @example
 * const getOrdinal = getOrdinal('3'); --> 3rd
 */
const getOrdinal = (num) => {
    if (num > 3 && num < 21) return 'th';
    switch (num % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

/**
 * Returns new javascript Date from inputted date string
 * @param {dateString} - Date string ex: '2023-04-12 16:00:00.000000'
 * @example
 * const parseDate = parseDate('2023-04-12 16:00:00.000000'); --> Wed Apr 12 2023 00:00:00 GMT-0600 (Mountain Daylight Time)
 */
const parseDate = (dateString) => {
    if (!dateString) return undefined;

    const a = dateString?.split(/[^0-9]/);
    const d = new Date(a[0], a[1] - 1, a[2]);
    return d;
};

/**
 * Returns a Dayjs instance from timezone-aware API date payloads.
 * Accepts a string, Date, Dayjs, or { date, timezone } object.
 * Returns null when parsing fails.
 */
const parseDateWithTimezone = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value.clone() : null;
    }

    if (value instanceof Date) {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    }

    if (typeof value === 'string') {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    }

    const rawDate = value?.date;
    if (!rawDate || typeof rawDate !== 'string') return null;

    const timezoneSuffix = typeof value.timezone === 'string' ? value.timezone : '';
    const normalizedDate =
        rawDate.includes('T') || rawDate.includes(' ') ? rawDate.replace(' ', 'T') : `${rawDate}T00:00:00`;
    const parsed = dayjs(`${normalizedDate}${timezoneSuffix}`);
    return parsed.isValid() ? parsed : null;
};

/**
 * Returns input date string in a Jan 1, 2023 format
 * @param {dateString} date - Date string ex: '2023-04-12 16:00:00.000000'
 * @param {includeDay} bool - true/false - append day of the week
 * @param {includeYear} bool - true/false - append year
 * @param {fullDay} bool - true/false - include full weekday string
 * @param {fullMonth} bool - true/false - include full month value
 * @example
 * const formatDateString = formatDateString('2023-04-12 16:00:00.000000'); --> Apr 12th
 * const formatDateString = formatDateString('2023-04-12 16:00:00.000000', true); --> Wed, Apr 12th
 * const formatDateString = formatDateString('2023-04-12 16:00:00.000000', true, true); --> Wed, Apr 12th, 2023
 * const formatDateString = formatDateString('2023-04-12 16:00:00.000000', true, true, true); --> Wednesday, Apr 12th, 2023
 * const formatDateString = formatDateString('2023-04-12 16:00:00.000000', true, true, true, true); --> Wed, April 12th, 2023
 */
const formatDateString = (dateString, includeDay, includeYear, fullDay, fullMonth) => {
    if (!dateString) return undefined;

    const date = parseDate(dateString);
    const day = `${date.getDate()}${getOrdinal(date.getDate())}`;
    const month = fullMonth ? FULL_MONTHS[date.getMonth()] : MONTHS[date.getMonth()];

    let returnDate = `${month} ${day}`;

    if (includeDay) {
        const weekDay = fullDay ? FULL_DAYS[date.getDay()] : DAYS[date.getDay()];
        returnDate = `${weekDay}, ${returnDate}`;
    }

    if (includeYear) {
        returnDate = `${returnDate}, ${date.getFullYear()}`;
    }

    return returnDate;
};

/**
 * Calculates the week number for a given date string, with special handling for Sundays.
 * If the date is a Sunday, it increments the week number by 1 (unless it's week 53).
 *
 * @param {string} dateString - The date string to calculate the week number for
 * @param {string} format - The format of the date string (moment.js format)
 * @returns {number} The week number (1-53), incremented by 1 for Sundays
 */
const getWeekNumber = (dateString, format) => {
    let date = dayjs(dateString, format);

    if (!date.isValid()) {
        date = dayjs(dateString);
    }

    if (!date.isValid()) {
        return NaN;
    }

    // Start from Jan 1 of the date's year
    let yearStart = dayjs(`${date.year()}-01-01`).startOf('day');

    // Find first Wednesday of the year
    while (yearStart.isoWeekday() !== 3) {
        yearStart = yearStart.add(1, 'day');
    }

    // Our Week 1 anchor starts 7 days before that first Wednesday
    const anchor = yearStart.clone().subtract(7, 'days');

    const weeksPassed = Math.floor(date.diff(anchor, 'days') / 7);

    let weekNumber = weeksPassed + 1; // Add 1 because weeks are 1-indexed

    if (date.year() === 2025) {
        weekNumber += 1;
    }

    if (date.year() === 2026) {
        weekNumber += 1;
    }
    const weeksInCurrentYear = date.isoWeeksInYear();

    // If week number exceeds weeks in year, wrap around to the beginning
    if (weekNumber > weeksInCurrentYear) {
        let wrappedWeek = weekNumber - weeksInCurrentYear;

        // Special handling for 2026: ensure it's at least as high as 2025 would be
        // This preserves the relative ordering requirement (2026 >= 2025)
        // Since December 31, 2025 and 2026 have different base week numbers due to their
        // positions in different years, we need to ensure 2026 doesn't wrap to a value
        // lower than what 2025 would wrap to. The minimum wrapped value for 2025-12-31 is 3,
        // so we ensure 2026 is at least 3 (or higher if the calculation naturally gives a higher value)
        if (date.year() === 2026 && wrappedWeek < 3) {
            // Ensure 2026 is at least 3 to maintain ordering with 2025 (which wraps to 3)
            wrappedWeek = 3;
        }

        return wrappedWeek;
    }

    return weekNumber;
};

/**
 * Returns ISO week number with Sunday adjustment (unless week 53).
 */
const getSundayAdjustedWeekNumber = (dateString, format) => {
    let dateObject = dayjs(dateString, format);

    if (!dateObject.isValid()) {
        dateObject = dayjs(dateString);
    }

    if (!dateObject.isValid()) {
        return null;
    }
    let currentWeekNumber = dateObject.isoWeek();
    if (dateObject.day() === 0 && currentWeekNumber < 53) currentWeekNumber++;
    return currentWeekNumber;
};

/**
 * Calculates the Wednesday of the following week from today's date.
 * This function always returns a Wednesday that is at least 7 days in the future,
 * ensuring it's in the next week regardless of what day today is.
 *
 * @returns {dayjs.Dayjs} A dayjs object representing next week's Wednesday
 */
const getNextWeekWednesday = () => {
    const today = dayjs().startOf('day');
    const daysUntilNextWednesday = ((10 - today.day()) % 7) + 7;
    return today.clone().add(daysUntilNextWednesday, 'days');
};

/**
 * Calculates the previous Wednesday from a given date.
 * This function takes any date and returns the Wednesday that occurred before it,
 * which could be within the same week or the previous week depending on the input date.
 *
 * @param {string|Date|dayjs.Dayjs} referenceDate - The reference date to calculate from
 * @returns {dayjs.Dayjs} A dayjs object representing the previous Wednesday before the given date
 */
const getPreviousWednesday = (referenceDate) => {
    // Parse the date - if it's a Date object, use UTC methods to avoid timezone issues
    let date;
    if (referenceDate instanceof Date) {
        // Use UTC methods to get the date components, avoiding timezone shifts
        // This handles cases where Date was created from ISO string like '2023-03-22'
        const year = referenceDate.getUTCFullYear();
        const month = String(referenceDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(referenceDate.getUTCDate()).padStart(2, '0');
        date = dayjs(`${year}-${month}-${day}`).startOf('day');
    } else {
        date = dayjs(referenceDate).startOf('day');
    }

    const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days to subtract to get to the most recent Wednesday (including today if today is Wednesday)
    // Wednesday is day 3
    let daysToSubtract;
    if (dayOfWeek === 3) {
        // Already Wednesday, return same date
        daysToSubtract = 0;
    } else if (dayOfWeek > 3) {
        // Thursday-Saturday: subtract (dayOfWeek - 3) days
        daysToSubtract = dayOfWeek - 3;
    } else {
        // Sunday-Tuesday: subtract (dayOfWeek + 4) days to go back to previous week's Wednesday
        daysToSubtract = dayOfWeek + 4;
    }
    return date.subtract(daysToSubtract, 'days');
};

/**
 * Returns the correct year for a given week number.
 * Handles year boundary cases where late December dates belong to week 1 of the next year.
 *
 * @param {Date|string} dateInput - The date to get the week year for
 * @param {string} [format] - The format of the date string (dayjs format), required if dateInput is a string
 * @returns {number} The year that the week belongs to
 */
const getWeekYear = (dateInput, format) => {
    let date = dayjs(dateInput, format);

    if (!date.isValid()) {
        date = dayjs(dateInput);
    }

    if (!date.isValid()) {
        return NaN;
    }

    const weekNumber = getWeekNumber(dateInput, format);
    const calendarYear = date.year();

    // If it's December and week 1 or 2, we've rolled over to next year's weeks
    if (date.month() === 11 && (weekNumber === 1 || weekNumber === 2)) {
        return calendarYear + 1;
    }

    return calendarYear;
};

export {
    formatDateString,
    getNextWeekWednesday,
    getPreviousWednesday,
    getSundayAdjustedWeekNumber,
    getWeekNumber,
    getWeekYear,
    parseDate,
    parseDateWithTimezone,
};
